import React, { useState } from 'react';
import styles from '../../pages/CheckoutPage.module.css';

const POPULAR_BANKS = [
  { code: 'SBIN', name: 'State Bank of India', color: '#1565c0' },
  { code: 'HDFC', name: 'HDFC Bank',           color: '#004c97' },
  { code: 'ICIC', name: 'ICICI Bank',           color: '#b71c1c' },
  { code: 'KKBK', name: 'Kotak Mahindra Bank',  color: '#ed1c24' },
  { code: 'AXIB', name: 'Axis Bank',            color: '#97144d' },
  { code: 'PUNB', name: 'Punjab National Bank', color: '#ff6400' },
  { code: 'CNRB', name: 'Canara Bank',          color: '#004f38' },
  { code: 'INDB', name: 'IndusInd Bank',        color: '#00478e' },
  { code: 'YESB', name: 'Yes Bank',             color: '#003479' },
  { code: 'IDFB', name: 'IDFC First Bank',      color: '#8b1a1a' },
];

// Fallback palette for banks with no known brand color — used for
// liveBanks entries (Cashfree's eligibility API has no color field).
const FALLBACK_COLORS = ['#1565c0', '#004c97', '#b71c1c', '#ed1c24', '#97144d', '#ff6400', '#004f38', '#00478e'];

// gateway prop added 2026-09-13 — Cashfree actually supports ~97 banks
// (cashfree.com/docs/payments/manage/payment-methods/netbanking), but this
// component's own POPULAR_BANKS list was scoped to match Razorpay's picker
// (10 banks) — a real coverage gap found live on STG.
//
// liveBanks prop added 2026-09-17 — CASHFREE_POPULAR_BANKS (the curated
// ~30-bank guess this replaces as the default) drifted from what's
// actually eligible on the real account (a listed bank got rejected by
// Cashfree live) — liveBanks carries the REAL, currently-enabled list
// (from Cashfree's own eligibility API, see cashfreeUtils.js::
// fetchCashfreeEligiblePaymentMethods), and is preferred whenever
// available. CASHFREE_POPULAR_BANKS is now only a fallback for when that
// fetch hasn't completed yet or failed. Razorpay's list/enabledBanks
// filtering is completely unchanged either way.
//
// Update 2026-10-17: the CASHFREE_POPULAR_BANKS fallback is gone. It was
// shown while the live list was still loading and whenever the fetch came
// back empty, offering banks (HDFC/SBI/Axis/Kotak...) that aren't enabled on
// the account — Cashfree then errored on the bank page. Cashfree now shows
// ONLY the live list; liveBanksLoaded tells "still loading" apart from
// "loaded but nothing enabled".
function NetbankingSection({ amount, processing, onPay, enabledBanks = [], gateway = 'razorpay', liveBanks = [], liveBanksLoaded = true }) {
  const [showAll, setShowAll] = useState(false);

  const cashfreeSource = liveBanks.map((b, i) => ({
    code: b.code,
    name: b.display || String(b.code),
    color: FALLBACK_COLORS[i % FALLBACK_COLORS.length],
  }));

  const bankSource = gateway === 'cashfree' ? cashfreeSource : POPULAR_BANKS;

  // enabledBanks (server-driven) is a Razorpay-specific concept — Cashfree's
  // list above (live or fallback) is shown as-is, no separate filtering.
  const visibleBanks = gateway !== 'cashfree' && enabledBanks.length > 0
    ? bankSource.filter((b) => enabledBanks.includes(b.code))
    : bankSource;

  const displayedBanks = showAll ? visibleBanks : visibleBanks.slice(0, 3);

  if (gateway === 'cashfree' && visibleBanks.length === 0) {
    return (
      <div className={styles.bankList}>
        <p style={{ margin: 0, padding: '12px 4px', fontSize: 13, color: '#6b7280', lineHeight: 1.5 }}>
          {liveBanksLoaded
            ? 'Netbanking is currently unavailable. Please use another payment method.'
            : 'Loading available banks…'}
        </p>
      </div>
    );
  }

  return (
    <div className={styles.bankList}>
      {displayedBanks.map((bank) => (
        <button
          key={bank.code}
          className={styles.bankListItem}
          onClick={() => onPay({ method: 'netbanking', bank: bank.code })}
          disabled={processing}
        >
          <span
            className={styles.bankListAvatar}
            style={{ background: bank.color }}
          >
            {bank.name.slice(0, 1)}
          </span>
          <span className={styles.bankListName}>{bank.name}</span>
          <span className={styles.bankListChevron}>›</span>
        </button>
      ))}

      {!showAll && visibleBanks.length > 3 && (
        <button className={styles.viewAllBtn} onClick={() => setShowAll(true)}>
          View all banks
        </button>
      )}
    </div>
  );
}

export default NetbankingSection;
